import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  getSizes,
  deleteSize,
  editSize,
  bulkDeleteSizes,
  bulkUpdateSizes,
} from "../../services/api";
import RetailerSidebar from "./RetailerSidebar";
import AddSizeModal from "./AddSize";
import colors from "../../theme/colors";
import toast, { Toaster } from "react-hot-toast";
import BulkDeleteConfirmModal from "./sizes/BulkDeleteConfirmModal";
import BulkUpdateModal from "./sizes/BulkUpdateModal";
import DeleteConfirmModal from "./sizes/DeleteConfirmModal";
import { showErrorToast, showSuccessToast } from "../../services/toast";
import { EditSizeModal } from "./sizes/EditSizeModal";

export default function ProductSizes() {
  const navigate = useNavigate();
  const [sizes, setSizes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [actionMenuOpen, setActionMenuOpen] = useState(null);
  const [draggedItemIndex, setDraggedItemIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [selectedSizeForEdit, setSelectedSizeForEdit] = useState(null);
  const [editFormData, setEditFormData] = useState({
    name: "",
    width: "",
    height: "",
    unit: "mm",
    default_layout: "grid",
    room_categories: [],
  });

  // ------------------------------------ Bulk Product Upload and Delete ------------------------------------
  const [showBulkUpdateModal, setShowBulkUpdateModal] = useState(false);
  const [bulkUpdateData, setBulkUpdateData] = useState({
    name: "",
    width: "",
    height: "",
    unit: "",
    default_layout: "",
    room_categories: [],
  });

  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(null);

  const openBulkDeleteModal = (sizeIds) => {
    if (!sizeIds?.length) return;
    setBulkDeleteConfirm(sizeIds);
  };

  const executeBulkDelete = async () => {
    if (!bulkDeleteConfirm || !bulkDeleteConfirm.length) return;
    setBulkDeleting(true);
    try {
      const response = await bulkDeleteSizes(bulkDeleteConfirm);
      toast.success(
        response.message ||
          `${response.deleted_count} product sizes deleted successfully`,
      );
      setBulkDeleteConfirm(null);
      setSelectedSizes([]);
      await fetchSizes();
    } catch (error) {
      console.error("Bulk delete error:", error);
      toast.error(error.message || "Failed to delete selected sizes");
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleBulkUpdate = async () => {
    if (!selectedSizes.length) {
      toast.error("Please select at least one product size.");
      return;
    }
    // Check if at least one field contains input
    const hasUpdateFields =
      bulkUpdateData.name.trim() !== "" ||
      bulkUpdateData.width !== "" ||
      bulkUpdateData.height !== "" ||
      bulkUpdateData.unit !== "" ||
      bulkUpdateData.default_layout !== "" ||
      bulkUpdateData.room_categories.length > 0;

    if (!hasUpdateFields) {
      toast.error("Please enter or select at least one field to update.");
      return;
    }

    // Build Payload
    const payload = { size_ids: selectedSizes };

    if (bulkUpdateData.name.trim() !== "")
      payload.name = bulkUpdateData.name.trim();
    if (bulkUpdateData.width !== "")
      payload.width = Number(bulkUpdateData.width);
    if (bulkUpdateData.height !== "")
      payload.height = Number(bulkUpdateData.height);
    if (bulkUpdateData.unit !== "") payload.unit = bulkUpdateData.unit;
    if (bulkUpdateData.default_layout !== "")
      payload.default_layout = bulkUpdateData.default_layout;
    if (bulkUpdateData.room_categories.length > 0)
      payload.room_categories = bulkUpdateData.room_categories;

    setBulkUpdating(true);

    try {
      const response = await bulkUpdateSizes(payload);
      toast.success(
        response.message ||
          `${response.updated_count} product sizes updated successfully`,
      );

      setShowBulkUpdateModal(false);
      setSelectedSizes([]);
      await fetchSizes();
    } catch (error) {
      console.error("Bulk update error:", error);
      toast.error(error.message || "Failed to update selected sizes");
    } finally {
      setBulkUpdating(false);
    }
  };

  const ROOM_CATEGORIES = [
    "Living Room",
    "Kitchen",
    "Bathroom",
    "Outdoor",
    "Bedroom",
    "Commercial",
    "Elevation",
    "Counter Tops",
    "Step-Riser",
    "Waterline",
  ];

  // ------------------------------------ Filter sizes based on search query ------------------------------------
  const filteredSizes = sizes.filter((size) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      size.name?.toLowerCase().includes(query) ||
      size.unit?.toLowerCase().includes(query) ||
      size.default_layout?.toLowerCase().includes(query)
    );
  });

  // ------------------------------------ Paginate sizes ------------------------------------
  const paginatedSizes = filteredSizes.slice(
    (currentPage - 1) * entriesPerPage,
    currentPage * entriesPerPage,
  );

  useEffect(() => {
    fetchSizes();
  }, []);

  const fetchSizes = async () => {
    try {
      const response = await getSizes();
      if (response.success) {
        setSizes(response.sizes);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ------------------------------------ Single Product Delete and Edit ------------------------------------
  const handleDelete = async () => {
    if (!deleteConfirm) return;

    setDeleting(true);
    try {
      // const token = localStorage.getItem("retailer_token");
      const response = await deleteSize(deleteConfirm);

      if (response?.success || response) {
        setSizes((prev) => prev.filter((s) => s.id !== deleteConfirm));

        // Use backend success message if available, fallback to default
        const successMessage =
          response?.message ||
          response?.detail ||
          "Product size deleted successfully!";

        showSuccessToast(successMessage);
        setDeleteConfirm(null);
      }
    } catch (err) {
      // Extract detail or message from API error response
      const errorMessage =
        err?.response?.data?.detail ||
        err?.data?.detail ||
        err?.detail ||
        err?.message ||
        "Failed to delete product size.";

      showErrorToast(errorMessage);
    } finally {
      setDeleting(false);
    }
  };

  // ------------------------------------ Drag & Drop Handlers ------------------------------------
  const handleDragStart = (e, index) => {
    setDraggedItemIndex(index);
    e.dataTransfer.effectAllowed = "move";
    // Required for Firefox and WebKit browsers to register the drop target correctly
    e.dataTransfer.setData("text/plain", index.toString());
  };

  const handleDragOver = (e, index) => {
    e.preventDefault(); // Critical: Allows dropping on this target
    e.dataTransfer.dropEffect = "move";
    if (dragOverIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    e.stopPropagation();

    setDragOverIndex(null);

    if (draggedItemIndex === null || draggedItemIndex === targetIndex) {
      setDraggedItemIndex(null);
      return;
    }

    // Calculate actual position index factoring in current pagination
    const realDraggedIndex =
      (currentPage - 1) * entriesPerPage + draggedItemIndex;
    const realTargetIndex = (currentPage - 1) * entriesPerPage + targetIndex;

    const updatedSizes = [...sizes];
    const [reorderedItem] = updatedSizes.splice(realDraggedIndex, 1);
    updatedSizes.splice(realTargetIndex, 0, reorderedItem);

    setSizes(updatedSizes);
    setDraggedItemIndex(null);

    // Optional: Send new order to backend API
    // updatePositionApi(updatedSizes);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <RetailerSidebar />
      <main className="flex-1 ml-64 p-6 w-full">
        <div className="w-full">
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-3xl font-bold text-gray-900">Product Sizes</h1>
            <button
              onClick={() => setAddModalOpen(true)}
              className="bg-accent hover:bg-accent2 text-white font-semibold py-3 px-6 rounded-lg transition-all"
            >
              Add Product Size
            </button>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
              {error}
            </div>
          )}

          {loading ? (
            <div className="text-center py-12">
              <p className="text-gray-500">Loading sizes...</p>
            </div>
          ) : sizes.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-12 shadow-sm text-center">
              <p className="text-gray-500 mb-4">No sizes added yet</p>
              <button
                onClick={() => setAddModalOpen(true)}
                className="bg-accent hover:bg-accent2 text-white font-semibold py-3 px-6 rounded-lg transition-all"
              >
                Add Your First Size
              </button>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
              {/* Table Header with Search and Filters */}
              <div className="p-4 border-b border-gray-200">
                <div className="flex justify-between items-center flex-wrap gap-4">
                  {/* BULK ACTION TOOLBAR & CONTROLS */}
                  <div className="mb-4 space-y-3">
                    {selectedSizes.length > 0 && (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openBulkDeleteModal(selectedSizes)}
                          disabled={bulkDeleting}
                          className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium text-sm rounded-lg transition-colors"
                        >
                          Delete ({selectedSizes.length})
                        </button>

                        <button
                          onClick={() => {
                            setBulkUpdateData({
                              name: "",
                              width: "",
                              height: "",
                              unit: "",
                              default_layout: "",
                              room_categories: [],
                            });
                            setShowBulkUpdateModal(true);
                          }}
                          disabled={bulkUpdating}
                          className="px-4 py-2 bg-indigo-600 text-white rounded-lg"
                        >
                          Bulk Update ({selectedSizes.length})
                        </button>

                        <button
                          onClick={() => setSelectedSizes([])}
                          className="px-3 py-2 text-gray-600"
                        >
                          Clear
                        </button>
                      </div>
                    )}

                    <BulkUpdateModal
                      open={showBulkUpdateModal}
                      bulkUpdateData={bulkUpdateData}
                      setBulkUpdateData={setBulkUpdateData}
                      onClose={() => setShowBulkUpdateModal(false)}
                      onSubmit={handleBulkUpdate}
                      loading={bulkUpdating}
                    />

                    <BulkDeleteConfirmModal
                      open={!!bulkDeleteConfirm}
                      selectedCount={bulkDeleteConfirm?.length || 0}
                      onClose={() => setBulkDeleteConfirm(null)}
                      onConfirm={executeBulkDelete}
                      loading={bulkDeleting}
                    />

                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <span>Show</span>

                      <select
                        value={entriesPerPage}
                        onChange={(e) =>
                          setEntriesPerPage(Number(e.target.value))
                        }
                        className="border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-accent"
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                      </select>

                      <span>entries</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Search Product Sizes..."
                        value={searchQuery}
                        onChange={(e) => {
                          setSearchQuery(e.target.value);
                          setCurrentPage(1);
                        }}
                        className="border border-gray-300 rounded-md p-2 pl-10 text-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent w-64"
                      />
                      <svg
                        className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={selectAll}
                          onChange={(e) => {
                            setSelectAll(e.target.checked);
                            if (e.target.checked) {
                              setSelectedSizes(filteredSizes.map((s) => s.id));
                            } else {
                              setSelectedSizes([]);
                            }
                          }}
                          className="w-4 h-4 text-accent border-gray-300 rounded focus:ring-accent"
                        />
                      </th>
                      <th
                        className="text-left px-4 py-3 text-sm font-semibold"
                        style={{ color: colors.t2 }}
                      >
                        No
                      </th>
                      <th
                        className="text-left px-4 py-3 text-sm font-semibold"
                        style={{ color: colors.t2 }}
                      >
                        Product Size
                      </th>
                      <th
                        className="text-left px-4 py-3 text-sm font-semibold"
                        style={{ color: colors.t2 }}
                      >
                        Products Added
                      </th>
                      <th
                        className="text-left px-4 py-3 text-sm font-semibold"
                        style={{ color: colors.t2 }}
                      >
                        Created Date
                      </th>
                      <th
                        className="text-left px-4 py-3 text-sm font-semibold"
                        style={{ color: colors.t2 }}
                      >
                        Status
                      </th>
                      <th
                        className="text-left px-4 py-3 text-sm font-semibold"
                        style={{ color: colors.t2 }}
                      >
                        Action
                      </th>
                      {/* <th
                        className="text-center px-4 py-3 text-sm font-semibold"
                        style={{ color: colors.t2 }}
                      >
                        Position
                      </th> */}
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedSizes.map((size, index) => (
                      <tr
                        key={size.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, index)}
                        className={`border-b border-gray-200 transition-all ${
                          draggedItemIndex === index
                            ? "opacity-40 bg-gray-100"
                            : ""
                        } ${
                          dragOverIndex === index && draggedItemIndex !== index
                            ? "border-t-2 border-accent bg-blue-50"
                            : "hover:bg-gray-50"
                        }`}
                      >
                        <td className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={selectedSizes.includes(size.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedSizes([...selectedSizes, size.id]);
                              } else {
                                setSelectedSizes(
                                  selectedSizes.filter((id) => id !== size.id),
                                );
                              }
                              setSelectAll(false);
                            }}
                            className="w-4 h-4 text-accent border-gray-300 rounded focus:ring-accent"
                          />
                        </td>
                        <td
                          className="px-4 py-3 text-sm"
                          style={{ color: colors.t3 }}
                        >
                          {(currentPage - 1) * entriesPerPage + index + 1}
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() =>
                              navigate("/retailer/tiles", {
                                state: { sizeFilter: size.id },
                              })
                            }
                            className="text-sm font-medium flex items-center gap-1 hover:text-accent transition-colors"
                            style={{ color: colors.t1 }}
                          >
                            {size.name}
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                              />
                            </svg>
                          </button>
                        </td>
                        <td
                          className="px-4 py-3 text-sm"
                          style={{ color: colors.t3 }}
                        >
                          {size.products_added || 0}
                        </td>
                        <td
                          className="px-4 py-3 text-sm"
                          style={{ color: colors.t3 }}
                        >
                          {size.created_at
                            ? new Date(size.created_at).toLocaleDateString()
                            : "-"}
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() =>
                              console.log("Toggle status:", size.id)
                            }
                            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                              size.status === "active"
                                ? "bg-green-100 text-green-700"
                                : "bg-gray-100 text-gray-600"
                            }`}
                          >
                            {size.status || "Active"}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <div className="relative">
                            <button
                              onClick={() =>
                                setActionMenuOpen(
                                  actionMenuOpen === size.id ? null : size.id,
                                )
                              }
                              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                              <svg
                                className="w-5 h-5 text-indigo-600 hover:text-indigo-800 transition-colors"
                                fill="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <circle cx="5" cy="12" r="2" />
                                <circle cx="12" cy="12" r="2" />
                                <circle cx="19" cy="12" r="2" />
                              </svg>
                            </button>
                            {actionMenuOpen === size.id && (
                              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                                <button
                                  onClick={() => setSelectedSizeForEdit(size)}
                                  className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors"
                                  style={{ color: colors.t2 }}
                                >
                                  Edit Size
                                </button>
                                <div>
                                  {/* 1. Add Toaster Provider at root level */}
                                  <Toaster />

                                  {/* 2. Action Menu Dropdown Button */}
                                  <button
                                    onClick={() => {
                                      setDeleteConfirm(size.id); // Store current item ID
                                      setActionMenuOpen(null); // Close dropdown menu
                                    }}
                                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                                  >
                                    Delete Size
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                        {/* <td className="px-4 py-3 text-center">
                          <div className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 inline-block p-1">
                            <svg
                              className="w-5 h-5 text-gray-700 hover:text-gray-900 transition-colors"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 3v18m0-18l-3 3m3-3l3 3m-3 15l-3-3m3 3l3-3M3 12h18m-18 0l3-3m-3 3l3 3m15-3l-3-3m3 3l-3 3"
                              />
                            </svg>
                          </div>
                        </td> */}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="p-4 border-t border-gray-200 flex justify-between items-center">
                <div className="text-sm" style={{ color: colors.t2 }}>
                  Showing{" "}
                  {filteredSizes.length > 0
                    ? (currentPage - 1) * entriesPerPage + 1
                    : 0}{" "}
                  to{" "}
                  {Math.min(currentPage * entriesPerPage, filteredSizes.length)}{" "}
                  of {filteredSizes.length} entries
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                    style={{ color: colors.t2 }}
                  >
                    Previous
                  </button>
                  {Array.from(
                    {
                      length: Math.ceil(filteredSizes.length / entriesPerPage),
                    },
                    (_, i) => i + 1,
                  ).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-1 border rounded text-sm transition-colors ${
                        currentPage === page ? "text-white" : "hover:bg-gray-50"
                      }`}
                      style={{
                        backgroundColor:
                          currentPage === page ? colors.accent : "",
                        color: currentPage === page ? "#fff" : colors.t2,
                        borderColor:
                          currentPage === page ? colors.accent : "#d1d5db",
                      }}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() =>
                      setCurrentPage(
                        Math.min(
                          Math.ceil(filteredSizes.length / entriesPerPage),
                          currentPage + 1,
                        ),
                      )
                    }
                    disabled={
                      currentPage ===
                        Math.ceil(filteredSizes.length / entriesPerPage) ||
                      filteredSizes.length === 0
                    }
                    className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                    style={{ color: colors.t2 }}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}

          <EditSizeModal
            isOpen={Boolean(selectedSizeForEdit)}
            sizeData={selectedSizeForEdit}
            onClose={() => setSelectedSizeForEdit(null)}
            onSuccess={fetchSizes}
          />

          <AddSizeModal
            isOpen={addModalOpen}
            onClose={() => setAddModalOpen(false)}
            onSuccess={fetchSizes}
          />

          {/* 3. Centered Delete Confirmation Modal */}
          <DeleteConfirmModal
            open={!!deleteConfirm}
            count={Array.isArray(deleteConfirm) ? deleteConfirm.length : 1}
            onClose={() => setDeleteConfirm(null)}
            onConfirm={handleDelete}
            loading={deleting}
          />
        </div>
      </main>
    </div>
  );
}
